import { Invoice, ServiceRequest } from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { IconSearch } from '@tabler/icons-react';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
} from '@/components/resource-search/ResourceSearchForm.types';
import { ResourceTableColumn } from '@/components/resource-search/ResourceTable';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { PaymentStatus } from '../lims-types';
import { ARKON } from '@consts/systems.consts';
import { formatHumanName, formatMoney, resolveId } from '@medplum/core';

// -----------------------------------------------------------------------------
export interface PaymentSearchParams {
  patientName?: string;
  status?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  testType?: string;
  testTypeDisplay?: string;
}

// -----------------------------------------------------------------------------
export const PAYMENT_SEARCH_DEFAULTS: PaymentSearchParams = {
  patientName: undefined,
  status: undefined,
  dateFrom: null,
  dateTo: null,
  testType: undefined,
  testTypeDisplay: undefined,
};

// -----------------------------------------------------------------------------
// FHIR Invoice statuses
const INVOICE_STATUSES = ['draft', 'issued', 'balanced'] as const;

// -----------------------------------------------------------------------------
export const paymentSearchConfig: ResourceSearchConfig<
  PaymentSearchParams,
  Invoice
> = {
  resourceType: 'Invoice',
  buildSearchQuery: (params, page, itemsPerPage) => {
    const query: string[][] = [
      ['_count', String(itemsPerPage)],
      ['_offset', String((page - 1) * itemsPerPage)],
      ['_sort', '-date'],
      ['_include', 'Invoice:recipient'],
      ['_include', 'Invoice:subject'],
    ];

    if (params.testType) {
      query.push(['type', params.testType]);
    }

    if (params.status) {
      query.push(['status', params.status]);
    }

    if (params.patientName) {
      query.push(['recipient:Patient.name', params.patientName]);
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query.push(['_filter', `(date ge ${from} and date le ${to})`]);
    } else if (params.dateFrom) {
      query.push(['date', `ge${params.dateFrom.toISOString().split('T')[0]}`]);
    } else if (params.dateTo) {
      query.push(['date', `le${params.dateTo.toISOString().split('T')[0]}`]);
    }
    return query;
  },
};

// -----------------------------------------------------------------------------
interface PaymentFieldOptions {
  t: TFunction<'lims'>;
  searchParams: PaymentSearchParams;
  onSearch: (updates: Partial<PaymentSearchParams>) => void;
  patientNameQuery: string;
  setPatientNameQuery: (value: string) => void;
  setPatientQuery: (value: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;
}

// -----------------------------------------------------------------------------
export const getPaymentSearchFields = ({
  t,
  searchParams,
  onSearch,
  patientNameQuery,
  setPatientNameQuery,
  setPatientQuery,
  patientNameOptions,
  patientLoading,
  patientNameLoading,
}: PaymentFieldOptions): SearchFieldDefinition<PaymentSearchParams>[] => {
  return [
    {
      key: 'patientName',
      type: 'autocomplete',
      placeholder: t(
        'paymentsPage.searchPlaceholder',
        'Search by patient name...',
        {
          ns: 'lims',
        }
      ),
      icon: <IconSearch size={16} />,
      value: patientNameQuery,
      options: patientNameOptions,
      loading: patientLoading || patientNameLoading,
      onQueryChange: (value: string) => {
        setPatientQuery(value);
        setPatientNameQuery(value);
        if (!value) {
          onSearch({ patientName: undefined });
        }
      },
      onOptionSubmit: (value: string) => onSearch({ patientName: value }),
      onEnter: (value: string) => onSearch({ patientName: value }),
    },
    {
      key: 'testType',
      type: 'codeableConcept',
      placeholder: t(
        'paymentsPage.testTypePlaceholder',
        'Search by test type',
        {
          ns: 'lims',
        }
      ),
      binding: ARKON.VS.GENETIC_TESTS_EL,
      defaultValue: searchParams.testType
        ? {
            coding: [
              {
                code: searchParams.testType,
                display: searchParams.testTypeDisplay,
              },
            ],
            text: searchParams.testTypeDisplay,
          }
        : undefined,
      onChange: (value: any) => {
        const code = value?.coding?.[0]?.code;
        const display = value?.coding?.[0]?.display || value?.text || undefined;
        onSearch({
          testType: code,
          testTypeDisplay: display,
        });
      },
    },
    {
      key: 'status',
      type: 'select',
      placeholder: t('paymentsPage.statusPlaceholder', 'Select status...', {
        ns: 'lims',
      }),
      options: INVOICE_STATUSES.map((status) => ({
        value: status,
        label: t(`payment.statuses.${status}`, status, { ns: 'lims' }),
      })),
      value: searchParams.status,
      onChange: (value: string | null) =>
        onSearch({ status: value || undefined }),
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('paymentsPage.fromDatePlaceholder', 'From date', {
        ns: 'lims',
      }),
      value: searchParams.dateFrom,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('paymentsPage.toDatePlaceholder', 'To date', {
        ns: 'lims',
      }),
      value: searchParams.dateTo,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<PaymentSearchParams>[];
}

// -----------------------------------------------------------------------------
interface PaymentColumnOptions {
  t: TFunction<'lims'>;
  patients: Map<string, any>;
  serviceRequests?: Map<string, ServiceRequest>;
  pendingAmounts?: Map<string, number>;
}


// -----------------------------------------------------------------------------
export const getPaymentTableColumns = ({
  t,
  patients,
}: PaymentColumnOptions): ResourceTableColumn<Invoice>[] => {
  return [
    {
      key: 'testType',
      label: t('paymentsPage.table.testType', 'Test Type', { ns: 'lims' }),
      render: (invoice) => {
        const orderType =
          invoice.type?.coding?.[0]?.display ||
          invoice.type?.text ||
          invoice.type?.coding?.[0]?.code;
        return orderType || '-';
      },
    },
    {
      key: 'patient',
      label: t('paymentsPage.table.patient', 'Patient', { ns: 'lims' }),
      render: (invoice) => {
        const patientRef = invoice.recipient?.reference || invoice.subject?.reference;
        const patientId = resolveId({ reference: patientRef }) ?? '';
        const patient = patients.get(patientId);
        if (!patient) {
          return t('paymentsPage.unknown', 'Unknown', { ns: 'lims' });
        }

        return (
          formatHumanName(patient?.name?.[0]) ||
          t('paymentsPage.unknown', 'Unknown', { ns: 'lims' })
        );
      },
    },
    {
      key: 'totalAmount',
      label: t('paymentsPage.table.totalAmount', 'Total Amount', {
        ns: 'lims',
      }),
      render: (invoice) => formatMoney(invoice.totalNet),
    },
    {
      key: 'status',
      label: t('paymentsPage.table.status', 'Status', { ns: 'lims' }),
      render: (invoice) => (
        <PaymentStatusBadge
          status={(invoice.status as PaymentStatus) || 'draft'}
        />
      ),
    },
    {
      key: 'date',
      label: t('paymentsPage.table.date', 'Date', { ns: 'lims' }),
      render: (invoice) => {
        const date = invoice.date || invoice.meta?.lastUpdated;
        return date ? new Date(date).toLocaleDateString('el-GR') : '-';
      },
    },
  ];
}
